import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormTextField } from "@bvhi/ui-base";
import { CancelIcon, UpdateIcon } from "@bvhi/ui-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { ReactNode, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, object, string } from "yup";
import { useKeyboardShortcut } from "../hooks/use-keyboard-shortcuts";

export interface ConfirmDialogConfig {
  title: string;
  subTitle?: string;
  additionalContent?: ReactNode;
  confirmationCode?: string;
  confirmButtonLabel?: string;
  confirmButtonColor?: "inherit" | "error" | "primary" | "secondary" | "info" | "success" | "warning";
  confirmButtonIcon?: ReactNode;
  cancelButtonLabel?: string;
  cancelButtonIcon?: ReactNode;
}

interface Props {
  overwrite: ConfirmDialogConfig;
  open: boolean;
  onClose: (conirmed: boolean) => void;
}

interface FormValues {
  confirmField: string;
}

export const SecuredConfirmDialog = ({ open, onClose, overwrite }: Props) => {
  const { t, i18n } = useTranslation(["dialogs", "validation"]);
  const cCode = (overwrite?.confirmationCode ?? t("dialogs:dialogs.confirm.dialog.confirm.field.value")).toLocaleUpperCase();

  const confirmSchema: AnyObjectSchema = object({
    confirmField: string()
      .matches(new RegExp(`^${cCode}$`, "i"), t("validation:exact"))
      .required(t("validation:required")),
  });

  const {
    formState: { isDirty, isValid, ...otherFormState },
    reset,
    control,
    ...otherMethods
  } = useForm<FormValues>({
    resolver: yupResolver(confirmSchema),
    mode: "all",
    context: { language: i18n.language },
    defaultValues: {
      confirmField: "",
    },
  });

  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  useKeyboardShortcut({
    key: "Enter",
    onKeyPressed: () => primaryButtonRef.current?.click(),
  });

  return (
    <Dialog
      disableRestoreFocus
      open={open}
      onClose={() => {
        reset();
        onClose(false);
      }}
    >
      <FormProvider control={control} formState={{ isDirty, isValid, ...otherFormState }} reset={reset} {...otherMethods}>
        <DialogTitle>{overwrite?.title}</DialogTitle>
        <DialogContent>
          {overwrite?.additionalContent}

          <DialogContentText pb={3}>{t("dialogs:dialogs.confirm.text", { confirmationCode: cCode })}</DialogContentText>

          <FormTextField
            autoFocus
            fullWidth
            label={t("dialogs:dialogs.confirm.dialog.form.confirm.text.field.label")}
            name="confirmField"
            placeholder={cCode}
            sx={{
              input: {
                textTransform: "uppercase",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={overwrite?.cancelButtonIcon ?? <CancelIcon />}
            variant="text"
            onClick={() => {
              reset();
              onClose(false);
            }}
          >
            {overwrite?.cancelButtonLabel ?? t("dialogs:dialogs.cancel")}
          </Button>
          <Button
            ref={primaryButtonRef}
            color={overwrite?.confirmButtonColor ?? "primary"}
            disabled={!isDirty || !isValid}
            startIcon={overwrite?.confirmButtonIcon ?? <UpdateIcon />}
            type="submit"
            variant="contained"
            onClick={() => {
              reset();
              onClose(true);
            }}
          >
            {overwrite?.confirmButtonLabel ?? t("dialogs:dialogs.confirm")}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
